import { Checkbox, InputNumber } from 'antd';
import React from 'react';
import { noneDecimalFormatter } from '../../../Helper';
import { ApplicationSetting, ConfigurationState, SettingsValue } from '../../../model/ConfigurationModel';
import styles from './securitySettings.module.scss';

interface Props {
	configuration: ConfigurationState;
	onSetSettingValue: (value: SettingsValue<string>) => void;
	onSetSettingsHaveChanged: (value: boolean) => void;
	castNumber: (value: number) => boolean;
}

const minValue: number = 1;

const SecuritySettings: React.FC<Props> = ({ configuration, onSetSettingValue, onSetSettingsHaveChanged, castNumber }) => {
	const setSettingsHaveChanged = () => {
		if (!haveSettingsChanged) {
			onSetSettingsHaveChanged(true);
		}
	};

	const lockoutAmountTime: ApplicationSetting = configuration.LockoutAmountTime;
	const lockoutMaxAttempts: ApplicationSetting = configuration.LockoutMaxAttempts;
	const lockoutPerIpAddress: ApplicationSetting = configuration.LockoutPerIpAddress;
	const lockoutPeriodAttackTime: ApplicationSetting = configuration.LockoutPeriodAttackTime;
	const haveSettingsChanged: boolean = configuration.HaveSettingsChanged;
	const globalSessionLabel = configuration.GlobalSessionTimeout.Label;
	const globalSessionValue = Number(configuration.GlobalSessionTimeout.Value);
	return (
		<div className={styles.container}>
			<div className={styles.info}>
				<div className={styles.gridRow}>
					<div className={styles.addBoldFont}>*{lockoutMaxAttempts.Label}</div>
					<InputNumber
						onChange={e => {
							const setValues: SettingsValue<string> = {
								key: 'Value',
								value: e.toString(),
								type: 'LockoutMaxAttempts',
							};
							onSetSettingValue(setValues);
							setSettingsHaveChanged();
						}}
						value={(lockoutMaxAttempts.Value as number) ?? 4}
						max={10}
						min={1}
						maxLength={2}
						id='LockoutMaxAttempts'
						formatter={noneDecimalFormatter}
					/>
					<div>
						<label htmlFor='LockoutMaxAttempts'>{_('Range1-10')}</label>
					</div>
				</div>
				<div className={styles.gridRow}>
					<div className={styles.addBoldFont}>*{lockoutPeriodAttackTime.Label}</div>
					<div>
						<InputNumber
							onChange={e => {
								const setValues: SettingsValue<string> = {
									key: 'Value',
									value: e.toString(),
									type: 'LockoutPeriodAttackTime',
								};
								onSetSettingValue(setValues);
								setSettingsHaveChanged();
							}}
							value={(lockoutPeriodAttackTime.Value as number) ?? 60}
							max={120}
							min={1}
							maxLength={3}
							id='LockoutPeriodAttackTime'
							formatter={noneDecimalFormatter}
						/>
					</div>
					<div>
						<label htmlFor='LockoutPeriodAttackTime'>{_('Range1-120s')}</label>
					</div>
				</div>
				<div className={styles.gridRow}>
					<div className={styles.addBoldFont}>*{lockoutAmountTime.Label}</div>
					<div>
						<InputNumber
							onChange={e => {
								const setValues: SettingsValue<string> = {
									key: 'Value',
									value: e.toString(),
									type: 'LockoutAmountTime',
								};
								onSetSettingValue(setValues);
								setSettingsHaveChanged();
							}}
							value={(lockoutAmountTime.Value as number) ?? 1}
							max={60}
							min={1}
							maxLength={2}
							id='LockoutAmountTime'
							formatter={noneDecimalFormatter}
						/>
					</div>
					<div>
						<label htmlFor='LockoutAmountTime'>{_('Range1-60m')}</label>
					</div>
				</div>
			</div>
			<div className={styles.lockupIpAddress}>
				<div className={styles.gridRow}>
					<div className={styles.addBoldFont}>
						<label htmlFor='LockoutPerIpAddress'>*{lockoutPerIpAddress.Label}</label>
					</div>
					<div>
						<Checkbox
							id='LockoutPerIpAddress'
							onChange={e => {
								const setValues: SettingsValue<string> = {
									key: 'Value',
									value: e.target.checked ? '1' : '0',
									type: 'LockoutPerIpAddress',
								};
								onSetSettingValue(setValues);
								setSettingsHaveChanged();
							}}
							checked={castNumber(Number(lockoutPerIpAddress.Value ?? 0))}
							aria-label={`*${lockoutPerIpAddress.Label}`}
						/>
					</div>
				</div>
				<div>
					<label>{_('LockoutIPNote')}</label>
				</div>
			</div>
			<div className={styles.globalSessionTimeout}>
				<div className={styles.addBoldFont}>
					<label htmlFor='GlobalSessionTimeout'>*{_('GlobalSessionTimeout')}</label>
				</div>
				<label>{_('Allowed')}</label>
				<InputNumber
					id='GlobalSessionTimeout'
					min={minValue}
					max={240}
					maxLength={3}
					value={globalSessionValue}
					onChange={value => {
						if (!value || value === undefined || value < minValue) {
							value = minValue;
						}

						const setValues: SettingsValue<string> = {
							key: 'Value',
							value: value.toString(),
							type: 'GlobalSessionTimeout',
						};
						onSetSettingValue(setValues);
						setSettingsHaveChanged();
					}}
				/>
				<div>
					<label htmlFor='GlobalSessionTimeout'>{globalSessionLabel}</label>
				</div>
			</div>
		</div>
	);
};

export { SecuritySettings };
